import { Text, TextField } from '@shared/components';
import { ActionTimelineEvent } from '@shared/components/events-timeline/components/action-timeline-event';
import { InlineTextArea } from '@shared/components/inline-text-area/InlineTextArea';
import { MovementStopDetails } from '@shared/types/movement';
import type { StopType as StandardStopType } from 'clerk_common/enums';
import { humanizeDate, humanizeTime } from '../util';

const STOP_TYPE_LABELS = {
  PICKUP: 'Pickup',
  DELIVERY: 'Delivery',
} satisfies Record<StandardStopType, string>;

type StopPreviewProps = {
  stop: MovementStopDetails;
  index: number;
};

export function StopPreview({ stop, index }: StopPreviewProps) {
  const stopTypeLabel = getStopTypeLabel(stop.stopType);
  const startDateTime = stop.dateTimeRanges?.[0]?.start?.dateTime;
  const startTimezone = stop.dateTimeRanges?.[0]?.start?.timezone;
  const humanDate = startDateTime
    ? humanizeDate(startDateTime, startTimezone ?? undefined)
    : undefined;

  const humanStartTime = startDateTime
    ? humanizeTime(startDateTime, startTimezone ?? undefined)
    : undefined;

  const endDateTime = stop.dateTimeRanges?.[0]?.end?.dateTime;
  const endTimezone = stop.dateTimeRanges?.[0]?.end?.timezone;
  const humanEndTime = endDateTime
    ? humanizeTime(endDateTime, endTimezone ?? undefined)
    : undefined;

  return (
    <ActionTimelineEvent type={stop.stopType}>
      <div className="mb-4 ml-2 mt-1">
        {stopTypeLabel && (
          <Text type="body-xs" className="mb-1 text-gray-500">
            {stopTypeLabel}
          </Text>
        )}
        {stop.location && (
          <Text isHeavy type="body-xs">
            {stop.location.city}, {stop.location.state}
          </Text>
        )}
        {stop.location && (
          <Text type="body-xs">{stop.location.addressOne}</Text>
        )}
      </div>
      <div className="mb-4 ml-2 mt-1">
        <div className="my-3 rounded-md border bg-gray-50 p-2">
          <InlineValueField
            label="Date"
            value={humanDate ?? ''}
            placeholder={humanDate ?? 'Not specified'}
          />
          <InlineValueField
            label="Start Time"
            value={humanStartTime ?? ''}
            placeholder={humanStartTime ?? 'Not specified'}
          />
          {humanEndTime && (
            <InlineValueField
              label="End Time"
              value={humanEndTime ?? ''}
              placeholder={humanEndTime ?? 'Not specified'}
            />
          )}
          <InlineTextArea
            name={`stops[${index}].externalNotes`}
            label="Notes"
            save={() => {}}
            required={false}
            className="mt-3"
            isDisabled={true}
            rows={2}
            placeholder={stop.externalNotes ?? 'Not specified'}
          />
        </div>
      </div>
    </ActionTimelineEvent>
  );
}

function getStopTypeLabel(
  stopType: MovementStopDetails['stopType']
): string | undefined {
  if (!stopType || stopType === 'NONE') return undefined;
  return STOP_TYPE_LABELS[stopType];
}

function InlineValueField({
  label,
  value,
  placeholder,
}: {
  label: string;
  value: string;
  placeholder: string;
}) {
  return (
    <div className="flex items-center gap-2">
      {label && (
        <Text type="body-sm" className="mr-12 whitespace-nowrap">
          {label}
        </Text>
      )}
      <TextField
        isDisabled={true}
        aria-label={label}
        size="medium"
        transparentWithoutFocus
        value={value}
        placeholder={placeholder}
      />
    </div>
  );
}
